import { defineStore } from 'pinia';
import { ref } from 'vue';
import { api, ApiCallError } from '@/api/client';
import { useSSE } from '@/composables/useSSE';
import type { Generation, RepeatOut, SseEventKind, StreamTokenOut } from '@/api/types';

type StreamEvent = { kind: SseEventKind; data: Record<string, unknown> };

export const useGenerationStore = defineStore('generation', () => {
  const current = ref<Generation | null>(null);
  const elapsedS = ref<number>(0);
  const etaMaxS = ref<number | null>(null);
  const error = ref<string | null>(null);
  let sseHandle: ReturnType<typeof useSSE<StreamEvent>> | null = null;

  async function load(id: string) {
    error.value = null;
    try {
      current.value = await api.get<Generation>(`/generations/${id}`);
    } catch (e) {
      error.value = e instanceof ApiCallError ? e.message : 'Не удалось загрузить';
      throw e;
    }
  }

  function watchProgress(id: string) {
    if (sseHandle) sseHandle.close();
    sseHandle = useSSE<StreamEvent>({
      url: `/api/v1${`/generations/${id}/stream`}`,
      pollFn: async () => {
        const g = await api.get<Generation>(`/generations/${id}`);
        if (g.status === 'completed') return { kind: 'done', data: { ...g } };
        if (g.status === 'failed') return { kind: 'failed', data: { ...g } };
        // Backfill an elapsed estimate from `created_at` and read the
        // ETA the API computed for us — the progress bar should advance
        // even when SSE never opened.
        const elapsed = g.created_at
          ? Math.max(0, Math.floor((Date.now() - new Date(g.created_at).getTime()) / 1000))
          : 0;
        return {
          kind: 'tick',
          data: {
            status: g.status,
            elapsed_s: elapsed,
            ...(g.eta_max_s != null ? { eta_max_s: g.eta_max_s } : {}),
          },
        };
      },
      tokenFn: async () => {
        const out = await api.post<StreamTokenOut>(
          `/generations/${id}/stream-token`,
        );
        return out.token;
      },
      parseEvent: (event, data) => ({
        kind: event as SseEventKind,
        data: (data ?? {}) as Record<string, unknown>,
      }),
      isTerminal: (t) => t.kind === 'done' || t.kind === 'failed',
    });
    // Wire reactive updates.
    const stop = setInterval(() => {
      const ev = sseHandle?.lastEvent.value;
      if (!ev || !current.value) return;
      if (ev.kind === 'tick') {
        if (typeof ev.data.elapsed_s === 'number') elapsedS.value = ev.data.elapsed_s;
        if (typeof ev.data.eta_max_s === 'number') etaMaxS.value = ev.data.eta_max_s;
        if (typeof ev.data.status === 'string') current.value.status = ev.data.status as Generation['status'];
      } else if (ev.kind === 'done') {
        current.value.status = 'completed';
        if (typeof ev.data.result_url === 'string') current.value.result_url = ev.data.result_url;
        if (typeof ev.data.preview_url === 'string') current.value.preview_url = ev.data.preview_url;
        clearInterval(stop);
      } else if (ev.kind === 'failed') {
        current.value.status = 'failed';
        if (typeof ev.data.error === 'string') current.value.error = ev.data.error;
        clearInterval(stop);
      }
    }, 200);
  }

  function stopWatch() {
    sseHandle?.close();
    sseHandle = null;
  }

  async function repeat(): Promise<RepeatOut | null> {
    if (!current.value) return null;
    return await api.post<RepeatOut>(`/generations/${current.value.id}/repeat`);
  }

  async function softDelete(): Promise<void> {
    if (!current.value) return;
    await api.delete(`/generations/${current.value.id}`);
  }

  return {
    current,
    elapsedS,
    etaMaxS,
    error,
    load,
    watchProgress,
    stopWatch,
    repeat,
    softDelete,
  };
});
